import { useEffect, useMemo, useState } from 'react';
import { ErrorState, LoadingState } from '../../components/StateBlock';
import { getPlatformSettings, type PlatformIssue, type PlatformSettings } from './platformApi';
import './platformSettings.css';

const STATE_LABEL: Record<string, string> = {
  ok: '正常',
  warning: '警告',
  error: '錯誤',
};

const STATE_CLASS: Record<string, string> = {
  ok: 'pill-good',
  warning: 'pill-warn',
  error: 'pill-danger',
};

// 用戶端應用狀態(client_apps.status = active | revoked)中文化 + 依實際狀態上 pill 色。
const APP_STATUS_LABEL: Record<string, string> = {
  active: '啟用中',
  revoked: '已撤銷',
};
const APP_STATUS_CLASS: Record<string, string> = {
  active: 'pill-good',
  revoked: 'pill-danger',
};

// 應用家族狀態(families.state = active | suspended)中文化 + 依實際狀態上 pill 色。
const FAMILY_STATE_LABEL: Record<string, string> = {
  active: '啟用中',
  suspended: '已暫停',
};
const FAMILY_STATE_CLASS: Record<string, string> = {
  active: 'pill-good',
  suspended: 'pill-warn',
};

function usePlatformSettings() {
  const [data, setData] = useState<PlatformSettings | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    getPlatformSettings(ac.signal)
      .then((r) => setData(r.data))
      .catch((err) => {
        if (!ac.signal.aborted) setError(err);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, []);

  return { data, error, loading };
}

function Pill({ state }: { state: string }) {
  // 平台驗證狀態 = 生命週期狀態 → 補 pill-dot 圖示化
  return (
    <span className={`pill pill-dot ${STATE_CLASS[state] ?? 'pill-neutral'}`}>{STATE_LABEL[state] ?? state}</span>
  );
}

interface KVRow {
  // 中文小標(主),對照 operator 熟悉的 raw env key(次要 mono),比照 reports 頁 detail-pair 處理
  gloss: string;
  key: string;
  value: string | number | boolean | null | undefined;
  // 次焦點:以 mono chip 強調(部署狀態 version/commit)
  chip?: boolean;
}

function KV({ rows }: { rows: KVRow[] }) {
  return (
    // 定義清單:term 欄(中文標 + raw key)固定寬 + 可換行,避免與 value(s3://…)擠在一起
    <dl className="adm-kv ps-kv">
      {rows.map(({ gloss, key, value, chip }) => {
        const empty = value === '' || value === null || value === undefined;
        const isPathish = typeof value === 'string' && (value.includes('://') || value.includes('.'));
        return (
          <div key={key} className="ps-kv-row">
            <dt className="ps-kv-term">
              <span className="ps-kv-gloss">{gloss}</span>
              <span className="ps-kv-key adm-mono">{key}</span>
            </dt>
            <dd className={!chip && isPathish ? 'adm-mono' : undefined}>
              {empty ? (
                <span className="text-tertiary">未設定</span>
              ) : chip ? (
                <span className="ps-chip adm-mono">{String(value)}</span>
              ) : (
                String(value)
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

// 驗證問題橫幅:沿用設計系統 inline-notice(警告=warn 琥珀、錯誤=danger 紅)
function Issues({ title, issues, tone }: { title: string; issues?: PlatformIssue[] | null; tone: 'error' | 'warning' }) {
  const rows = issues ?? [];
  if (rows.length === 0) return null;
  const toneClass = tone === 'error' ? 'ps-notice--danger' : 'inline-notice--warn';
  return (
    <section className={`inline-notice ps-notice ${toneClass}`} data-testid={`platform-${tone}s`}>
      <span className="ps-notice-icon" aria-hidden="true">
        {tone === 'error' ? '⛔' : '⚠'}
      </span>
      <div className="ps-notice-body">
        <div className="ps-notice-title">{title}</div>
        <ul className="ps-notice-list">
          {rows.map((issue) => (
            <li key={`${issue.code}-${issue.message}`}>
              <span className="adm-mono">{issue.code}</span>
              <span>{issue.message}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// 機密狀態(configured / not-managed-by-rest / missing)pill 化:
// 比照 monitoring 機密摘要,以狀態色取代裸文字,一眼看出是否缺失。
const SECRET_STATUS: Record<string, { label: string; cls: string }> = {
  configured: { label: '已設定', cls: 'pill-good' },
  'not-managed-by-rest': { label: '非 REST 託管', cls: 'pill-neutral' },
  missing: { label: '缺失', cls: 'pill-danger' },
};
function SecretPill({ value }: { value: string }) {
  const meta = SECRET_STATUS[value] ?? { label: value, cls: 'pill-neutral' };
  return <span className={`pill pill-dot ${meta.cls}`}>{meta.label}</span>;
}

function CapabilityList({ values }: { values: string[] }) {
  return (
    <div className="ps-cap-list">
      {values.map((v) => (
        <span key={v} className="pill pill-neutral">
          {v}
        </span>
      ))}
    </div>
  );
}

export default function PlatformSettingsPage() {
  const { data, error, loading } = usePlatformSettings();
  const secretRows = useMemo(
    () => Object.entries(data?.secrets ?? {}).sort(([a], [b]) => a.localeCompare(b)),
    [data?.secrets]
  );

  if (loading) return <LoadingState label="載入平台設定…" />;
  if (error || !data) return <ErrorState error={error} />;

  // 主狀態色條依驗證結果著色
  const heroTone =
    data.status.validation_state === 'ok'
      ? 'is-good'
      : data.status.validation_state === 'error'
        ? 'is-danger'
        : 'is-warn';

  return (
    <div className="ps-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">平台設定</h1>
          <p className="page-subtitle">平台基本資料、服務網域、種子資料與部署狀態。</p>
        </div>
      </div>

      {/* 平台身份主狀態 hero:品牌名 + 驗證結果 + 來源/環境 */}
      <div className={`status-hero ps-hero ${heroTone}`}>
        <div className="ps-hero-main">
          <h2 className="status-hero-title">{data.platform.brand_name || data.platform.platform_name}</h2>
          <p className="status-hero-detail">
            <span className="adm-mono">{data.platform.platform_id}</span>
            {' · '}
            <span className="adm-mono">{data.platform.primary_domain}</span>
          </p>
        </div>
        <div className="ps-hero-meta">
          <Pill state={data.status.validation_state} />
          <span className="pill pill-info">來源 {data.source}</span>
          <span className="pill pill-neutral">環境 {data.environment}</span>
        </div>
      </div>

      <Issues title="設定驗證錯誤" issues={data.validation.errors} tone="error" />
      <Issues title="設定驗證警告" issues={data.validation.warnings} tone="warning" />

      <section className="ps-grid">
        <div className="card">
          <h2 className="adm-card-title">平台身份</h2>
          <KV
            rows={[
              { gloss: '平台識別碼', key: 'platform_id', value: data.platform.platform_id },
              { gloss: '平台名稱', key: 'platform_name', value: data.platform.platform_name },
              { gloss: '品牌名稱', key: 'brand_name', value: data.platform.brand_name },
              { gloss: '主要網域', key: 'primary_domain', value: data.platform.primary_domain },
              { gloss: '預設區域', key: 'default_region', value: data.platform.default_region },
              { gloss: 'AWS 區域', key: 'aws_region', value: data.platform.aws_region },
            ]}
          />
        </div>

        <div className="card">
          <h2 className="adm-card-title">部署狀態</h2>
          <KV
            rows={[
              { gloss: '服務名稱', key: 'service', value: data.status.service },
              { gloss: '版本', key: 'version', value: data.status.version, chip: true },
              { gloss: '提交雜湊', key: 'commit', value: data.status.commit, chip: true },
              { gloss: '建置時間', key: 'build_time', value: data.status.build_time },
              { gloss: '設定版次', key: 'config_revision', value: data.status.config_revision },
              { gloss: '結構版本', key: 'schema_version', value: data.schema_version },
            ]}
          />
        </div>
      </section>

      <section className="ps-grid">
        <div className="card">
          <h2 className="adm-card-title">服務網域</h2>
          <KV
            rows={[
              { gloss: 'API 網域', key: 'api', value: data.domains.api },
              { gloss: '管理後台', key: 'admin', value: data.domains.admin },
              { gloss: '網頁應用', key: 'webapp', value: data.domains.webapp },
              { gloss: '開發者入口', key: 'devportal', value: data.domains.devportal },
              { gloss: '信令(App)', key: 'signaling_app', value: data.domains.signaling_app },
              { gloss: '信令(裝置)', key: 'signaling_device', value: data.domains.signaling_device },
              { gloss: '公開基底 URL', key: 'public_base_url', value: data.domains.public_base_url },
            ]}
          />
        </div>

        <div className="card card--muted">
          <h2 className="adm-card-title">Cookie、儲存與機密</h2>
          <KV
            rows={[
              { gloss: 'Refresh Cookie 網域', key: 'refresh_cookie_domain', value: data.cookies.refresh_cookie_domain },
              { gloss: 'Cookie 路徑', key: 'refresh_cookie_path', value: data.cookies.refresh_cookie_path },
              { gloss: 'SameSite 屬性', key: 'same_site', value: data.cookies.same_site },
              { gloss: 'Secure 旗標', key: 'secure', value: data.cookies.secure },
              { gloss: '匯出儲存桶', key: 'export_bucket_ref', value: data.storage.export_bucket_ref },
              { gloss: '開發者文件儲存桶', key: 'developer_docs_bucket_ref', value: data.storage.developer_docs_bucket_ref },
              { gloss: '錄影儲存桶', key: 'recording_bucket_ref', value: data.storage.recording_bucket_ref },
            ]}
          />
          {secretRows.length > 0 && (
            <>
              <h3 className="ps-subhead">機密狀態</h3>
              <dl className="adm-kv ps-kv" data-testid="platform-secrets">
                {secretRows.map(([k, v]) => (
                  <div key={k} className="ps-kv-row">
                    <dt className="adm-mono text-tertiary">{k}</dt>
                    <dd>
                      <SecretPill value={String(v)} />
                    </dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </div>
      </section>

      <section className="ps-section">
        <div className="section-head">
          <h2 className="section-title">用戶端應用種子</h2>
          <span className="section-meta">共 {data.client_apps.length} 個</span>
        </div>
        {data.client_apps.length === 0 ? (
          <div className="adm-inline-empty" data-testid="platform-client-apps-empty">
            <span className="adm-inline-empty-icon">📱</span>
            <span>尚無用戶端應用種子資料。</span>
          </div>
        ) : (
        <div className="table-scroll">
          <table className="data-table ps-table" data-testid="platform-client-apps">
            <thead>
              <tr>
                <th>應用 ID</th>
                <th>平台</th>
                <th>家族</th>
                <th>金鑰參照</th>
                <th>狀態</th>
                <th>能力</th>
              </tr>
            </thead>
            <tbody>
              {data.client_apps.map((app) => (
                <tr key={app.client_id}>
                  <td className="adm-mono">{app.client_id}</td>
                  <td>{app.platform}</td>
                  <td className="adm-mono">{app.family_id}</td>
                  <td>
                    <span className={`pill ${app.has_app_key_ref ? 'pill-good' : 'pill-neutral'}`}>
                      {app.has_app_key_ref ? '已設定金鑰' : '環境投影'}
                    </span>
                  </td>
                  <td>
                    <span className={`pill pill-dot ${APP_STATUS_CLASS[app.status] ?? 'pill-neutral'}`}>
                      {APP_STATUS_LABEL[app.status] ?? app.status}
                    </span>
                  </td>
                  <td>
                    <CapabilityList values={app.capabilities} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </section>

      <section className="ps-section">
        <div className="section-head">
          <h2 className="section-title">應用家族種子</h2>
          <span className="section-meta">共 {data.families.length} 個</span>
        </div>
        {data.families.length === 0 ? (
          <div className="adm-inline-empty" data-testid="platform-families-empty">
            <span className="adm-inline-empty-icon">🗂️</span>
            <span>尚無應用家族種子資料。</span>
          </div>
        ) : (
        <div className="table-scroll">
          <table className="data-table ps-family-table" data-testid="platform-families">
            <thead>
              <tr>
                <th>家族 ID</th>
                <th>名稱</th>
                <th>擁有者範圍</th>
                <th>狀態</th>
              </tr>
            </thead>
            <tbody>
              {data.families.map((family) => (
                <tr key={family.family_id}>
                  <td className="adm-mono">{family.family_id}</td>
                  <td>{family.name}</td>
                  <td className="adm-mono">{family.owner_scope}</td>
                  <td>
                    <span className={`pill pill-dot ${FAMILY_STATE_CLASS[family.state] ?? 'pill-neutral'}`}>
                      {FAMILY_STATE_LABEL[family.state] ?? family.state}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </section>
    </div>
  );
}
